/**
 * The BFF routes resolve the consultant from the session, so the browser sends
 * recipient data only. Transport failures map to each route's failure value.
 */
export const GIFT_OFFER_ROUTES = {
	eligibility: "/api/gift-offer-eligibility",
	claim: "/api/gift-offer-claim"
} as const;

export type EligibilityStatus = "eligible" | "not-eligible" | "unknown";

export interface EligibilityResponse {
	status: EligibilityStatus;
	reason?: string;
}

export type ClaimStatus = "success" | "not-eligible" | "failure";

export interface ClaimResponse {
	status: ClaimStatus;
}

export interface ClaimRequest {
	firstName: string;
	lastName: string;
	email: string;
}

export interface GiftOfferClient {
	checkEligibility(email: string): Promise<EligibilityResponse>;
	submitClaim(request: ClaimRequest): Promise<ClaimResponse>;
}

type FetchFn = typeof fetch;

// A slow route must never hold the form hostage: the page fails open on
// eligibility and shows a retryable error on claim.
const REQUEST_TIMEOUT_MS = 5000;

const ELIGIBILITY_STATUSES: ReadonlySet<string> = new Set([
	"eligible",
	"not-eligible",
	"unknown"
]);

const CLAIM_STATUSES: ReadonlySet<string> = new Set([
	"success",
	"not-eligible",
	"failure"
]);

async function postJson<T>(
	fetchFn: FetchFn,
	url: string,
	body: unknown
): Promise<T | undefined> {
	try {
		const response = await fetchFn(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
			signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS)
		});
		if (!response.ok) return undefined;
		return (await response.json()) as T;
	} catch {
		return undefined;
	}
}

export function createGiftOfferClient(
	fetchFn: FetchFn = (...args) => fetch(...args)
): GiftOfferClient {
	return {
		async checkEligibility(email) {
			const data = await postJson<EligibilityResponse>(
				fetchFn,
				GIFT_OFFER_ROUTES.eligibility,
				{ email }
			);
			if (!data || !ELIGIBILITY_STATUSES.has(data.status)) {
				return { status: "unknown" };
			}
			return { status: data.status, reason: data.reason };
		},

		async submitClaim(request) {
			const data = await postJson<ClaimResponse>(
				fetchFn,
				GIFT_OFFER_ROUTES.claim,
				request
			);
			if (!data || !CLAIM_STATUSES.has(data.status)) {
				return { status: "failure" };
			}
			return { status: data.status };
		}
	};
}
